import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { NavBar } from 'components/NavBar/NavBar'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getIncidents } from 'features/operations/incidentsSlice'
import { getPermits } from 'features/operations/permitsSlice'
import { AppDispatch, RootState } from 'store'
import { Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { ISafetyBreakdownRow, ISafetyToneProps } from 'types/safetyHub.interface'
import {
    AlarmBanner,
    AlarmContent,
    AlarmDot,
    AlarmText,
    BoardGrid,
    BreakdownList,
    BreakdownRow,
    KpiStrip,
    RowLabel,
    RowValue,
} from './Safety.styled'

const rowTone = (row: ISafetyBreakdownRow): ISafetyToneProps['$tone'] =>
    row.count > 0 ? row.tone : 'muted'

const Breakdown = ({ rows }: { rows: ISafetyBreakdownRow[] }) => (
    <BreakdownList>
        {rows.map((row) => (
            <BreakdownRow key={row.label}>
                <RowLabel $tone={rowTone(row)} $max={row.max && row.count > 0}>{row.label}</RowLabel>
                <RowValue $tone={rowTone(row)} $max={row.max && row.count > 0}>{row.count}</RowValue>
            </BreakdownRow>
        ))}
    </BreakdownList>
)

export const Safety = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const incidentRowsApi = useSelector((s: RootState) => s.incidents.data)
    const permitRowsApi = useSelector((s: RootState) => s.permits.permits.data)

    useEffect(() => {
        dispatch(getIncidents({ page: 1, limit: 500 }))
        dispatch(getPermits({ page: 1, limit: 500 }))
    }, [dispatch])

    const incidents = useMemo(() => {
        const open = incidentRowsApi.filter((i) => i.status !== 4)
        return {
            l1Minor: open.filter((i) => i.severityLevel === 1).length,
            l2Moderate: open.filter((i) => i.severityLevel === 2).length,
            l3Serious: open.filter((i) => i.severityLevel === 3).length,
            l4Fatal: open.filter((i) => i.severityLevel === 4).length,
        }
    }, [incidentRowsApi])

    const permits = useMemo(() => {
        const now = Date.now()
        return {
            draft: permitRowsApi.filter((p) => p.status === 1).length,
            issued: 0,
            active: permitRowsApi.filter((p) => p.status === 2).length,
            suspended: permitRowsApi.filter((p) => p.status === 3).length,
            closed: permitRowsApi.filter((p) => p.status === 4).length,
            expired: permitRowsApi.filter((p) => p.status !== 4 && new Date(p.expiresAt).getTime() < now).length,
        }
    }, [permitRowsApi])

    const zones = { operational: 0, held: 0, evacuated: 0 }
    const fatigue = { low: 0, elevated: 0, high: 0, critical: 0 }

    const openIncidents = incidents.l1Minor + incidents.l2Moderate + incidents.l3Serious + incidents.l4Fatal
    const seriousOpen = incidents.l3Serious + incidents.l4Fatal
    const hasFatal = incidents.l4Fatal > 0

    const alarmText = hasFatal
        ? `FATAL INCIDENT OPEN — ${incidents.l4Fatal} L4 incident${incidents.l4Fatal === 1 ? '' : 's'} on site. Emergency response is active. The Operations Director is notified automatically for L3+.`
        : `${incidents.l3Serious} serious (L3) incident${incidents.l3Serious === 1 ? '' : 's'} open — investigation in progress. The Operations Director is notified automatically for L3+.`

    const incidentPillText = hasFatal
        ? `${incidents.l4Fatal} fatal`
        : incidents.l3Serious > 0
            ? `${incidents.l3Serious} serious`
            : null

    const zoneRows: ISafetyBreakdownRow[] = [
        { label: 'Operational', count: zones.operational, tone: 'primary' },
        { label: 'Held', count: zones.held, tone: 'warning' },
        { label: 'Evacuated', count: zones.evacuated, tone: 'danger' },
    ]

    const permitRows: ISafetyBreakdownRow[] = [
        { label: 'Draft', count: permits.draft, tone: 'primary' },
        { label: 'Issued', count: permits.issued, tone: 'info' },
        { label: 'Active', count: permits.active, tone: 'success' },
        { label: 'Suspended', count: permits.suspended, tone: 'warning' },
        { label: 'Closed', count: permits.closed, tone: 'primary' },
        { label: 'Expired', count: permits.expired, tone: 'primary' },
    ]

    const incidentRows: ISafetyBreakdownRow[] = [
        { label: 'L1 – Minor', count: incidents.l1Minor, tone: 'info' },
        { label: 'L2 – Moderate', count: incidents.l2Moderate, tone: 'warning' },
        { label: 'L3 – Serious', count: incidents.l3Serious, tone: 'danger' },
        { label: 'L4 – Fatal / Catastrophic', count: incidents.l4Fatal, tone: 'danger', max: true },
    ]

    const fatigueRows: ISafetyBreakdownRow[] = [
        { label: 'Low', count: fatigue.low, tone: 'primary' },
        { label: 'Elevated', count: fatigue.elevated, tone: 'warning' },
        { label: 'High', count: fatigue.high, tone: 'danger' },
        { label: 'Critical', count: fatigue.critical, tone: 'danger', max: true },
    ]

    return <>
        <NavBar />
        <PageWrapper>
            <Flex center gap='0.75rem'>
                <H1WithBack title='Safety' arrow />
            </Flex>
            {seriousOpen > 0 && (
                <AlarmBanner $fatal={hasFatal}>
                    <AlarmContent>
                        <AlarmDot />
                        <AlarmText $fatal={hasFatal}>{alarmText}</AlarmText>
                    </AlarmContent>
                    <Button variant='danger' size='sm' onClick={() => navigate('/operations/safety/incidents')}>
                        View incidents
                    </Button>
                </AlarmBanner>
            )}
            <KpiStrip>
                <MetricCard
                    value={zones.held}
                    label='ZONES HELD'
                    accentColor={zones.held > 0 ? tokens.status.warning.fg : tokens.status.success.fg}
                    trailing={zones.held > 0
                        ? <StatusPill status='warning' dot>Attention</StatusPill>
                        : <StatusPill status='success'>All clear</StatusPill>}
                />
                <MetricCard value={permits.active} label='ACTIVE PERMITS' accentColor={tokens.accent.base} />
                <MetricCard
                    value={permits.suspended}
                    label='SUSPENDED PERMITS'
                    accentColor={permits.suspended > 0 ? tokens.status.warning.fg : tokens.accent.base}
                    trailing={permits.suspended > 0 ? <StatusPill status='warning'>Review</StatusPill> : undefined}
                />
                <MetricCard
                    value={openIncidents}
                    label='OPEN INCIDENTS'
                    accentColor={seriousOpen > 0 ? tokens.status.danger.fg : tokens.accent.base}
                    trailing={incidentPillText ? <StatusPill status='danger' dot>{incidentPillText}</StatusPill> : undefined}
                />
            </KpiStrip>
            <BoardGrid>
                <Container>
                    <ContainerHeader>
                        <Flex width='100%' between center >
                            <TitleText weight='bold' >Zone Statuses</TitleText>
                            <OtherText onClick={() => navigate('zone-tree')} style={{ color: tokens.accent.base, cursor: 'pointer', }}> Open tree </OtherText>
                        </Flex>
                    </ContainerHeader>
                    <ContainerContent>
                        <BodyText grey >Distribution across the 4-level hierarchy</BodyText>
                        <Breakdown rows={zoneRows} />
                    </ContainerContent>
                </Container>

                <Container>
                    <ContainerHeader>
                        <Flex width='100%' between center>
                            <TitleText weight='bold' >Permits</TitleText>
                            <OtherText onClick={() => navigate('permits')} style={{ color: tokens.accent.base, cursor: 'pointer', }}> View all </OtherText>
                        </Flex>
                    </ContainerHeader>
                    <ContainerContent>
                        <BodyText grey >Distribution by lifecycle status</BodyText>
                        <Breakdown rows={permitRows} />
                    </ContainerContent>
                </Container>

                <Container>
                    <ContainerHeader>
                        <Flex width='100%' between center>
                            <TitleText weight='bold' >Incidents</TitleText>
                            <OtherText onClick={() => navigate('incidents')} style={{ color: tokens.accent.base, cursor: 'pointer', }}> View all </OtherText>
                        </Flex>
                    </ContainerHeader>
                    <ContainerContent>
                        <BodyText grey >Open events by severity (excludes CLOSED)</BodyText>
                        <Breakdown rows={incidentRows} />
                    </ContainerContent>
                </Container>

                <Container>
                    <ContainerHeader>
                        <Flex width='100%' between center>
                            <TitleText weight='bold' >Fatigue</TitleText>
                            <OtherText onClick={() => navigate('fatigue')} style={{ color: tokens.accent.base, cursor: 'pointer', }}> View all </OtherText>
                        </Flex>
                    </ContainerHeader>
                    <ContainerContent>
                        <BodyText grey >At-risk workers by fatigue index and 7-day hours.</BodyText>
                        <Breakdown rows={fatigueRows} />
                    </ContainerContent>
                </Container>
            </BoardGrid>
            <OtherText color={tokens.text.muted} style={{ marginTop: '2rem' }}>
                Escalation rules are configured by HonestDig during onboarding.
            </OtherText>
        </PageWrapper>
    </>
}
